// Every time in the repair-centre pipeline is a British shop-floor wall clock.
//
// The exported report writes completion times as Excel serial numbers, and an
// Excel serial carries no timezone at all - 46239.6949211806 simply means
// "05/08/2026 16:40:41", the time the engineer saw on the wall. The bonus rule
// ("Mon-Fri, 08:00-17:30") is written against that same wall clock.
//
// So both ends must be pinned to Europe/London rather than to whatever zone the
// browser or Node process happens to be in. Anchoring to the IANA zone (instead
// of a hardcoded +0/+1) means BST transitions - and any future change to when or
// whether the UK shifts its clocks - come from the platform's timezone database
// and need no code change here.
export const LONDON_TZ = "Europe/London";

// Bonus window, in minutes past midnight London time. Both ends inclusive.
export const BONUS_WINDOW_START_MINUTES = 8 * 60; // 08:00
export const BONUS_WINDOW_END_MINUTES = 17 * 60 + 30; // 17:30

const londonFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: LONDON_TZ,
  hour12: false,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

function isValidDate(date) {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * The London wall clock for an instant: { year, month (1-12), day, hour,
 * minute, second, weekday (0=Sun) }. Returns null for an invalid date.
 *
 * Deliberately avoids getHours()/getDay() and friends - those read the host
 * machine's zone, which is exactly the bug this module exists to prevent.
 */
export function londonWallClock(date) {
  if (!isValidDate(date)) return null;
  const parts = {};
  for (const { type, value } of londonFormat.formatToParts(date)) {
    if (type !== "literal") parts[type] = value;
  }
  // Some engines render midnight as "24" rather than "00".
  const hour = parts.hour === "24" ? 0 : Number(parts.hour);
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  return {
    year,
    month,
    day,
    hour,
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
}

/** Europe/London's UTC offset, in minutes, at a given instant. */
export function londonOffsetMinutes(date) {
  const wall = londonWallClock(date);
  if (!wall) return 0;
  const millis = ((date.getTime() % 1000) + 1000) % 1000;
  const asIfUTC = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
    millis,
  );
  return (asIfUTC - date.getTime()) / 60000;
}

/**
 * The instant at which London's wall clock reads the given date and time.
 * `month` is 1-12.
 *
 * Two passes: guess with the offset in force at the naive instant, then settle
 * with the offset actually in force at the guess. That second pass is what
 * makes the two changeover weekends come out right.
 *
 * On the spring-forward Sunday, 01:00-01:59 never happens; such a time resolves
 * forward into BST. On the autumn-back Sunday, 01:00-01:59 happens twice; the
 * first (BST) occurrence wins. Both fall on a weekend, so neither can affect
 * bonus eligibility.
 */
export function fromLondonWallClock(
  year,
  month,
  day,
  hour = 0,
  minute = 0,
  second = 0,
  millis = 0,
) {
  const naive = Date.UTC(year, month - 1, day, hour, minute, second, millis);
  const firstGuess = naive - londonOffsetMinutes(new Date(naive)) * 60000;
  const settled = naive - londonOffsetMinutes(new Date(firstGuess)) * 60000;
  return new Date(settled);
}

// Excel's epoch, including the phantom 1900 leap day it counts.
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86400 * 1000;

// "2026-08-05T16:40:41Z" / "...+01:00" - a real instant, already unambiguous.
const ABSOLUTE_ISO = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?\s*(Z|[+-]\d{2}:?\d{2})$/i;
// "2026-08-05 16:40:41" / "2026-08-05T16:40" - a bare wall clock, no zone.
const NAIVE_ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?)?$/;
// "8/5/2026 4:40:41 PM" - month first, as the legacy CSV export writes it.
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i;

/**
 * Parse a completion timestamp from the repair-centre export into a real
 * instant. Excel serials, bare date-time text and month-first dates are all
 * read as London wall clocks; text carrying an explicit Z or UTC offset is
 * already an instant and is taken at face value.
 */
export function parseRepairCompletedDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const serialNum = parseFloat(raw);
  if (
    !isNaN(serialNum) &&
    isFinite(serialNum) &&
    serialNum > 40000 &&
    serialNum < 100000
  ) {
    // The serial's wall clock lands in the UTC fields of this scratch date;
    // re-read those fields as London time to get the true instant.
    const scratch = new Date(EXCEL_EPOCH_UTC + serialNum * MS_PER_DAY);
    return fromLondonWallClock(
      scratch.getUTCFullYear(),
      scratch.getUTCMonth() + 1,
      scratch.getUTCDate(),
      scratch.getUTCHours(),
      scratch.getUTCMinutes(),
      scratch.getUTCSeconds(),
      scratch.getUTCMilliseconds(),
    );
  }

  if (ABSOLUTE_ISO.test(raw)) {
    const d = new Date(raw);
    return isValidDate(d) ? d : null;
  }

  const naive = raw.match(NAIVE_ISO);
  if (naive) {
    return fromLondonWallClock(
      Number(naive[1]),
      Number(naive[2]),
      Number(naive[3]),
      Number(naive[4] || 0),
      Number(naive[5] || 0),
      Number(naive[6] || 0),
      Number(String(naive[7] || "0").padEnd(3, "0").slice(0, 3)),
    );
  }

  const us = raw.match(US_DATE);
  if (us) {
    let hour = parseInt(us[4] || "0", 10);
    const marker = String(us[7] || "").toUpperCase();
    if (marker === "PM" && hour < 12) hour += 12;
    if (marker === "AM" && hour === 12) hour = 0;
    return fromLondonWallClock(
      Number(us[3]),
      Number(us[1]),
      Number(us[2]),
      hour,
      parseInt(us[5] || "0", 10),
      parseInt(us[6] || "0", 10),
    );
  }

  const fallback = new Date(raw);
  return isValidDate(fallback) ? fallback : null;
}

/** True when a part was completed inside the bonus-earning shift. */
export function isRepairBonusWindow(date) {
  const wall = londonWallClock(date);
  if (!wall) return true;
  if (wall.weekday === 0 || wall.weekday === 6) return false;
  const minutes = wall.hour * 60 + wall.minute;
  return (
    minutes >= BONUS_WINDOW_START_MINUTES &&
    minutes <= BONUS_WINDOW_END_MINUTES
  );
}

/** True when a part was completed on a Saturday or Sunday, London time. */
export function isLondonWeekend(date) {
  const wall = londonWallClock(date);
  if (!wall) return false;
  return wall.weekday === 0 || wall.weekday === 6;
}

/** "YYYY-MM" for the London month an instant falls in. */
export function monthKeyFromDate(date) {
  const wall = londonWallClock(date);
  if (!wall) return "";
  return `${wall.year}-${String(wall.month).padStart(2, "0")}`;
}

/** "dd/mm, HH:MM" for display, always in London time. */
export function formatLondonShort(date) {
  if (!isValidDate(date)) return "";
  return date.toLocaleString("en-GB", {
    timeZone: LONDON_TZ,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** "YYYY-MM-DD" for the London day an instant falls in. */
export function londonDateKey(date) {
  const wall = londonWallClock(date);
  if (!wall) return "";
  const month = String(wall.month).padStart(2, "0");
  const day = String(wall.day).padStart(2, "0");
  return `${wall.year}-${month}-${day}`;
}

/** "HH:00" for the London hour an instant falls in. */
export function londonHourKey(date) {
  const wall = londonWallClock(date);
  if (!wall) return "";
  return `${String(wall.hour).padStart(2, "0")}:00`;
}

/** 0 (Sunday) - 6 (Saturday) for the London day an instant falls in. */
export function londonWeekdayIndex(date) {
  const wall = londonWallClock(date);
  return wall ? wall.weekday : null;
}
